import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Stack,
  FormHelperText,
  Card,
  CardContent,
  Typography,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon, Add as AddIcon, Star as StarIcon, Settings as SettingsIcon } from '@mui/icons-material';
import { useProject } from '../contexts/ProjectContext';
import { useCoins } from '../contexts/CoinContext';
import useLLMModels from '../hooks/useLLMModels';
import * as api from '../services/api';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';

const INITIAL_FORM_DATA = {
  post_type: 'article',
  query: '',
  limit: 5,
  llm_model: ''
};

const GenerateIdeasForm = ({ onGenerate, onClose }) => {
  const { selectedProject } = useProject();
  const { models, loading: modelsLoading } = useLLMModels();
  const { updateCoins } = useCoins();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(() => {
    // Initialize with default model if available in project settings
    const defaultModel = selectedProject?.settings?.default_idea_generator_model || 
                        selectedProject?.settings?.default_llm_model || 
                        'gpt-4o-mini';
    return {
      ...INITIAL_FORM_DATA,
      llm_model: defaultModel
    };
  });
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  // Load project settings when component mounts
  useEffect(() => {
    if (selectedProject?.id) {
      const loadSettings = async () => {
        try {
          const settings = await api.getProjectSettings(selectedProject.id);
          const defaultModel = settings.default_idea_generator_model || 
                             settings.default_llm_model || 
                             'gpt-4o-mini';
          setFormData(prev => ({
            ...prev,
            llm_model: defaultModel
          }));
        } catch (error) {
          console.error('Error loading project settings:', error);
        }
      };
      loadSettings();
    }
  }, [selectedProject?.id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.post_type || !formData.query) {
      setError('Please fill in all required fields');
      return;
    }

    if (!selectedProject) {
      setError('No project selected');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await api.generateIdeas({
        post_type: formData.post_type,
        project_id: selectedProject.id,
        query: formData.query,
        limit: parseInt(formData.limit),
        llm_model: formData.llm_model || null
      });

      onGenerate(response.ideas);
      
      // Reset form but preserve model selection
      setFormData(prev => ({ 
        ...INITIAL_FORM_DATA, 
        llm_model: prev.llm_model 
      }));
      
      // Update coins balance
      await updateCoins();
      
      // Show credit usage from response metadata
      const creditsUsed = response.metadata?.credits_used ?? 0;
      enqueueSnackbar(`-${creditsUsed.toFixed(2)} credits used`, { 
        variant: 'info',
        autoHideDuration: 3000,
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'left'
        }
      });
    } catch (error) {
      console.error('Error generating ideas:', error);
      if (error.response?.status === 402) {
        setError('Insufficient credits. Please add more credits to continue.');
      } else {
        const errorMessage = error.response?.data?.detail 
          ? (typeof error.response.data.detail === 'string' 
              ? error.response.data.detail 
              : JSON.stringify(error.response.data.detail))
          : 'Error generating ideas';
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <Card variant="outlined">
      <CardContent>
        <form onSubmit={handleSubmit}>
          <Stack spacing={2}>
            <Stack 
              direction={{ xs: 'column', md: 'row' }} 
              spacing={2} 
              alignItems={{ xs: 'stretch', md: 'center' }}
            >
              <TextField
                label="Topic or Query"
                name="query"
                value={formData.query}
                onChange={handleChange}
                required
                fullWidth
                disabled={loading}
                placeholder="E.g., 'Social media marketing tips' or 'Productivity hacks'"
                size="small"
              />

              <FormControl 
                sx={{ 
                  width: { xs: '100%', md: 300 }
                }}
                size="small"
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ flex: 1 }}>
                    <InputLabel>AI Model</InputLabel>
                    <Select
                      name="llm_model"
                      value={formData.llm_model}
                      label="AI Model"
                      onChange={handleChange}
                      disabled={loading || modelsLoading}
                      displayEmpty
                    >
                      {models.map((model) => (
                        <MenuItem 
                          key={model.name} 
                          value={model.name}
                          sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}
                        >
                          <Tooltip title={model.description || ''} placement="right">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <span>{model.name} ({model.provider})</span>
                              {model.name === selectedProject?.settings?.default_idea_generator_model && (
                                <Typography variant="caption" color="primary.main" sx={{ fontStyle: 'italic' }}>
                                  (Default)
                                </Typography>
                              )}
                              {model.premium && (
                                <StarIcon 
                                  sx={{ 
                                    fontSize: 16, 
                                    color: 'warning.main'
                                  }} 
                                />
                              )}
                            </Box>
                          </Tooltip>
                        </MenuItem>
                      ))}
                    </Select>
                  </Box>
                  <Tooltip title="Set default model in project settings">
                    <IconButton
                      size="small"
                      onClick={() => navigate(`/settings?highlight=default_idea_generator_model`)}
                      sx={{ color: 'primary.main' }}
                    >
                      <SettingsIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </FormControl>

              <Button
                type="submit"
                variant="contained"
                disabled={loading}
                startIcon={loading ? <CircularProgress size={16} /> : <AddIcon />}
                size="small"
                sx={{ 
                  width: { xs: '100%', md: 150 },
                  height: 40,
                }}
              >
                Generate
              </Button>
            </Stack>

            {error && (
              <FormHelperText error>{error}</FormHelperText>
            )}
          </Stack>
        </form>
      </CardContent>
    </Card>
  );
};

export default GenerateIdeasForm; 